import { arrayMove } from '@dnd-kit/sortable';
import type { ChecklistItem, ChecklistFormData } from '../types';

export const parseChecklistData = (blobText: string): ChecklistItem[] => {
  try {
    if (!blobText || blobText.trim() === '') {
      return [];
    }
    const parsed = JSON.parse(blobText);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to parse checklist data:', error);
    return [];
  }
};

export const stringifyChecklistData = (items: ChecklistItem[]): string => {
  try {
    return JSON.stringify(items);
  } catch (error) {
    console.error('Failed to stringify checklist data:', error);
    return '[]';
  }
};

export const generateChecklistItemId = (): string => {
  return Date.now().toString() + Math.random().toString(36).substr(2, 9);
};

export const sortBySequence = (items: ChecklistItem[]): ChecklistItem[] => {
  return [...items].sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
};

export const addChecklistItem = (
  items: ChecklistItem[],
  newItem: ChecklistFormData
): ChecklistItem[] => {
  const newChecklistItem: ChecklistItem = {
    id: generateChecklistItemId(),
    seq: newItem.seq,
    text: newItem.text
  };

  return sortBySequence([...items, newChecklistItem]);
};

export const updateChecklistItem = (
  items: ChecklistItem[],
  id: string,
  updates: Partial<ChecklistItem>
): ChecklistItem[] => {
  const updatedItems = items.map(item =>
    item.id === id ? { ...item, ...updates } : item
  );

  return sortBySequence(updatedItems);
};

export const deleteChecklistItem = (
  items: ChecklistItem[],
  id: string
): ChecklistItem[] => {
  const filteredItems = items.filter(item => item.id !== id);
  return resequenceItems(filteredItems);
};

export const resequenceItems = (items: ChecklistItem[]): ChecklistItem[] => {
  const sorted = sortBySequence(items);
  return sorted.map((item, index) => ({
    ...item,
    seq: (index + 1).toString()
  }));
};

export const validateChecklistItem = (
  formData: ChecklistFormData
): string[] => {
  const errors: string[] = [];

  if (!formData.text?.trim()) {
    errors.push('Text is required');
  }

  if (!formData.seq?.trim()) {
    errors.push('Sequence is required');
  } else {
    const seqNum = parseInt(formData.seq);
    if (isNaN(seqNum) || seqNum <= 0) {
      errors.push('Sequence must be a positive number');
    }
  }

  return errors;
};

export const resequenceAllItems = (items: ChecklistItem[]): ChecklistItem[] => {
  const sorted = sortBySequence(items);
  return sorted.map((item, index) => ({
    ...item,
    seq: String(index + 1)
  }));
};

export const addItemAtPosition = (
  items: ChecklistItem[],
  newItem: ChecklistFormData,
  position: number
): ChecklistItem[] => {
  const newChecklistItem: ChecklistItem = {
    id: generateChecklistItemId(),
    seq: String(position + 1),
    text: newItem.text
  };

  const updatedItems = [...items];
  updatedItems.splice(position, 0, newChecklistItem);

  return resequenceAllItems(updatedItems);
};

export const reorderAndResequence = (
  items: ChecklistItem[],
  activeId: string,
  overId: string
): ChecklistItem[] => {
  const oldIndex = items.findIndex(item => item.id === activeId);
  const newIndex = items.findIndex(item => item.id === overId);

  if (oldIndex === -1 || newIndex === -1) return items;

  const reordered = arrayMove(items, oldIndex, newIndex);

  return reordered.map((item, index) => ({
    ...item,
    seq: String(index + 1)
  }));
};
